import React, { useState } from "react";
import styled from "styled-components";
import PlayIcon from "../imgs/play.svg";

interface YoutubeBlockProps {
    url: string;
}

const extractYoutubeId = (input: string): string | null => {
    const url = String(input || "").trim();
    if (!url) return null;

    const primaryMatch = url.match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([^?&/]+)/,
    );

    const id = primaryMatch?.[1]?.trim();
    if (id) return id;

    const queryMatch = url.match(/[?&]v=([^&]+)/);
    return queryMatch?.[1]?.trim() || null;
};

export const YoutubeBlock = ({url}: YoutubeBlockProps) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const videoId = extractYoutubeId(url);

    if (!videoId) {
        if (!url) return null;
        return (
            <YoutubeFallback>
                <a href={url} target="_blank" rel="noopener noreferrer">
                    Watch on YouTube
                </a>
            </YoutubeFallback>
        );
    }

    const embedUrl = `https://www.youtube.com/embed/${videoId}${isPlaying ? '?autoplay=1' : ''}`;
    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

    return (
        <VideoContainer>
            {!isPlaying && (
                <ThumbnailOverlay onClick={() => setIsPlaying(true)}>
                    <Thumbnail src={thumbnailUrl} alt="Video thumbnail" />
                    <PlayButton>
                        <img src={PlayIcon} alt="Play" />
                    </PlayButton>
                </ThumbnailOverlay>
            )}
            {isPlaying && (
                <YoutubeFrame
                    src={embedUrl}
                    title="YouTube video"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            )}
        </VideoContainer>
    );
};

const VideoContainer = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    margin: 24px 0;
`;

const ThumbnailOverlay = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    cursor: pointer;
`;

const Thumbnail = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const PlayButton = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    img {
        width: 88px;
        height: 88px;
        transition: transform 0.2s ease;
        filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3));
    }

    &:hover img {
        transform: scale(1.1);
    }
`;

const YoutubeFrame = styled.iframe`
    width: 100%;
    height: 100%;
    border: none;
    background: #000;
`;

const YoutubeFallback = styled.div`
    margin: 24px 0;

    a {
        color: #16a34a;
        text-decoration: underline;
    }
`;
